import React, { useState, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import {
  Button,
  ErrorMessage,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  PDFContainer,
  Spinner,
} from "./styles";

// Configura o worker do PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

interface TermsModalProps {
  pdfUrl: string;
  onAccept: () => void;
  onClose?: () => void;
}

const TermsModal: React.FC<TermsModalProps> = ({
  pdfUrl,
  onAccept,
  onClose,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [isLastPageVisible, setIsLastPageVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const lastPageRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [pdfWidth, setPdfWidth] = useState<number>(0);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setIsLoading(false);
  }

  function onDocumentLoadError(error: Error) {
    console.error("Erro ao carregar o PDF:", error);
    setLoadError("Erro ao carregar o PDF. Por favor, tente novamente.");
    setIsLoading(false);
  }

  // Atualiza a largura do PDF com base na largura do container
  useEffect(() => {
    const updateWidth = () => {
      if (pdfContainerRef.current) {
        // Considera o padding se necessário
        setPdfWidth(pdfContainerRef.current.clientWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => {
      window.removeEventListener("resize", updateWidth);
    };
  }, []);

  // Utiliza IntersectionObserver para detectar a visibilidade da última página
  useEffect(() => {
    const container = document.getElementById("pdf-scroll-container");
    if (!container || !lastPageRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsLastPageVisible(entry.isIntersecting);
        });
      },
      {
        root: container,
        threshold: 0.0,
      }
    );

    observer.observe(lastPageRef.current);

    return () => {
      if (lastPageRef.current) {
        observer.unobserve(lastPageRef.current);
      }
    };
  }, [lastPageRef, numPages]);

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        tabIndex={-1}
      >
        <ModalHeader>
          <h1>Termos e Condições </h1>
        </ModalHeader>
        <PDFContainer id="pdf-scroll-container" ref={pdfContainerRef}>
          {isLoading && <Spinner />}
          {loadError && <ErrorMessage>{loadError}</ErrorMessage>}
          {!loadError && (
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={<Spinner />}
              error={<ErrorMessage>Erro ao carregar o PDF.</ErrorMessage>}
            >
              {Array.from(new Array(numPages), (_, index) => {
                const pageNumber = index + 1;
                // Atribui a ref à última página para monitoramento
                if (pageNumber === numPages) {
                  return (
                    <div key={`page_${pageNumber}`} ref={lastPageRef}>
                      <Page pageNumber={pageNumber} width={pdfWidth} />
                    </div>
                  );
                }
                return (
                  <Page
                    key={`page_${pageNumber}`}
                    pageNumber={pageNumber}
                    width={pdfWidth}
                  />
                );
              })}
            </Document>
          )}
        </PDFContainer>
        <Button disabled={!isLastPageVisible} onClick={onAccept}>
          Aceitar Termos e Condições
        </Button>
      </ModalContent>
    </ModalOverlay>
  );
};

export default TermsModal;
